import { useState } from 'react';
import { useApiResource } from '@/hooks/useApiResource';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Wallet, TrendingUp, TrendingDown, Target, Trash2, ArrowDownCircle, ArrowUpCircle, Bell,
} from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { AppHeader } from '@/components/AppHeader';
import { Chip, ProgressBar, SectionTitle, EmptyState } from '@/components/Primitives';
import { FullScreenLoader } from '@/components/FullScreenLoader';
import { FAB, FormDrawer, TextField, SelectField } from '@/components/FormControls';
import api from '@/lib/api';
import { formatPLN, formatDate, financeAmountColor, financeSign } from '@/lib/format';
import { useLang } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

const TYPES = ['income', 'expense', 'payment'];
const emptyItem = { type: 'income', title: '', amount: '', dueDate: '', status: 'Received' };
const cardEnter = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 } };

const defaultStatusForType = (type) => {
  if (type === 'income') return 'Received';
  if (type === 'expense') return 'Paid';
  return 'Pending';
};

const typeMeta = {
  income: { icon: ArrowUpCircle, color: 'text-emerald-300', chip: 'bg-emerald-400/10 text-emerald-200 border-emerald-300/20' },
  expense: { icon: ArrowDownCircle, color: 'text-rose-300', chip: 'bg-rose-400/10 text-rose-200 border-rose-300/20' },
  payment: { icon: Bell, color: 'text-amber-300', chip: 'bg-amber-400/10 text-amber-200 border-amber-300/20' },
};

export default function Finance() {
  const { t, tEnum, lang } = useLang();
  const { data, loading, reload: load } = useApiResource(
    async () => {
      const [s, l] = await Promise.all([api.get('/finance/summary'), api.get('/finance')]);
      return { summary: s.data, items: l.data };
    },
    { errorMessage: t('finance.loadError'), deps: [lang] }
  );
  const summary = data?.summary || null;
  const items = data?.items || [];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState(emptyItem);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const openCreate = () => { setForm(emptyItem); setDrawerOpen(true); };

  const save = async () => {
    if (!form.title.trim() || form.amount === '' || isNaN(Number(form.amount))) {
      toast.error(t('finance.invalid'));
      return;
    }
    setSaving(true);
    try {
      await api.post('/finance', {
        type: form.type, title: form.title, amount: Number(form.amount),
        dueDate: form.dueDate || null, status: form.status,
      });
      toast.success(t('finance.saved'));
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(t('finance.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/finance/${deleteId}`);
      toast.success(t('finance.deleted'));
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(t('finance.deleteError'));
    }
  };

  if (loading || !summary) return <FullScreenLoader label={t('finance.loader')} />;
  const remNeg = summary.remaining < 0;

  const kpis = [
    { tid: 'finance-monthly-goal-card', label: t('finance.monthlyGoal'), value: summary.monthlyIncomeGoal, color: 'text-violet-300', icon: Target },
    { tid: 'finance-income-card', label: t('finance.income'), value: summary.currentIncome, color: 'text-emerald-300', icon: TrendingUp },
    { tid: 'finance-expenses-card', label: t('finance.expenses'), value: summary.fixedExpenses, color: 'text-foreground/90', icon: TrendingDown },
    { tid: 'finance-remaining-card', label: t('finance.remaining'), value: summary.remaining, color: remNeg ? 'text-rose-300' : 'text-emerald-300', icon: Wallet },
  ];

  return (
    <AppLayout>
      <AppHeader title={t('finance.title')} subtitle={t('finance.subtitle')} back />

      <div className="mt-3 grid grid-cols-2 gap-3">
        {kpis.map((k, i) => (
          <motion.div
            key={k.tid}
            initial={cardEnter.initial}
            animate={cardEnter.animate}
            transition={{ duration: 0.3, delay: i * 0.04 }}
            data-testid={k.tid}
            className="glass-card rounded-2xl p-4"
          >
            <div className="flex items-center justify-between">
              <p className="text-[11px] text-foreground/55">{k.label}</p>
              <k.icon className={cn('h-4 w-4', k.color)} />
            </div>
            <p className={cn('mt-2 font-mono-plex text-lg font-semibold', k.color)}>{formatPLN(k.value)}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-4 glass-card rounded-2xl p-4">
        <div className="flex items-center justify-between text-xs text-foreground/55">
          <span>{t('finance.incomeGoalProgress')}</span>
          <span className="font-mono-plex text-violet-300">{summary.incomeGoalPct}%</span>
        </div>
        <ProgressBar value={summary.incomeGoalPct} tone="violet" className="mt-1.5" />
      </div>

      <div className="mt-4 rounded-2xl border border-brand-cyan/20 bg-brand-cyan/[0.08] p-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-brand-cyan">{t('finance.nextMoneyAction')}</p>
        <p className="mt-1.5 text-sm leading-6 text-foreground/90">{summary.nextMoneyAction}</p>
      </div>

      <div className="mt-5">
        <SectionTitle>{t('finance.recent')}</SectionTitle>
        <div className="space-y-2">
          {items.length === 0 ? (
            <EmptyState icon={Wallet} title={t('finance.emptyTitle')} subtitle={t('finance.emptySubtitle')} />
          ) : (
            items.map((it) => {
              const meta = typeMeta[it.type] || typeMeta.income;
              return (
                <div key={it.id} data-testid={`finance-item-${it.id}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-3">
                  <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.06]', meta.color)}>
                    <meta.icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground/90">{it.title}</p>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <Chip className={meta.chip}>{tEnum(it.type)}</Chip>
                      {it.dueDate ? <span className="text-[11px] text-foreground/45">{formatDate(it.dueDate, lang)}</span> : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={cn('font-mono-plex text-sm font-semibold', financeAmountColor(it.type))}>
                      {financeSign(it.type)}{formatPLN(it.amount)}
                    </span>
                    <button onClick={() => setDeleteId(it.id)} className="text-foreground/35 hover:text-rose-300">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <FAB onClick={openCreate} testId="finance-add-transaction-button" label={t('finance.addFab')} />

      <FormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={t('finance.drawerTitle')}
        onSubmit={save}
        loading={saving}
        submitLabel={t('common.save')}
        submitTestId="finance-save-button"
      >
        <SelectField
          label={t('finance.type')}
          testId="finance-type-select"
          value={form.type}
          onValueChange={(v) => setForm({ ...form, type: v, status: defaultStatusForType(v) })}
          options={TYPES}
        />
        <TextField label={t('finance.fieldTitle')} testId="finance-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t('finance.titlePlaceholder')} />
        <TextField label={t('finance.amount')} testId="finance-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0" />
        <TextField label={t('finance.date')} testId="finance-date-input" type="date" value={form.dueDate || ''} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
      </FormDrawer>

      {deleteId ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-6" onClick={() => setDeleteId(null)}>
          <div className="w-full max-w-[420px] rounded-2xl border border-white/10 bg-[#0A1730] p-5" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-semibold text-foreground">{t('finance.confirmTitle')}</p>
            <p className="mt-1 text-xs text-foreground/55">{t('common.cannotUndo')}</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setDeleteId(null)} className="flex-1 rounded-xl border border-white/10 bg-white/[0.06] py-2.5 text-sm font-medium text-foreground">{t('common.cancel')}</button>
              <button data-testid="finance-delete-confirm" onClick={doDelete} className="flex-1 rounded-xl bg-rose-500 py-2.5 text-sm font-medium text-white">{t('common.delete')}</button>
            </div>
          </div>
        </div>
      ) : null}
    </AppLayout>
  );
}
